import { useCustomer } from '@order/Customer';
import type { BillingAccount, PaymentMethod } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { usePaymentMethodCards } from '../usePaymentMethodCards';

/**
 * Depending on the feature flag, this will return either:
 * - Only Billing Accounts from `currentCustomer.billingAccounts` (from cache).
 * - Both Billing Accounts and Payment Method Cards from `usePaymentMethodCards`.
 */
export function usePaymentMethods(): PaymentMethodsResponse {
  const shouldUsePaymentMethodCards = useFeatureFlag(
    'CELS-1109-use-payment-svc-for-payment-method-operations',
  );

  const { customer } = useCustomer();
  const billingAccounts =
    customer?.billingAccounts ?? BILLING_ACCOUNTS_FALLBACK;

  const { paymentMethodCards, isLoadingPaymentMethodCards } =
    usePaymentMethodCards({ pause: !shouldUsePaymentMethodCards });

  return {
    paymentMethods: shouldUsePaymentMethodCards
      ? paymentMethodCards
      : billingAccounts,

    isLoadingPaymentMethods: shouldUsePaymentMethodCards
      ? isLoadingPaymentMethodCards
      : false,
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BILLING_ACCOUNTS_FALLBACK: readonly BillingAccount[] = [];

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentMethodsResponse = Readonly<{
  paymentMethods: readonly PaymentMethod[];
  isLoadingPaymentMethods: boolean;
}>;
